"use client"

import { useState, useCallback, useRef, useEffect } from "react"
import type { EnemyVariant } from "@/types/enemy.types"

export interface NewEnemyNotification {
  enemyId: string
  variant: EnemyVariant
}

export const useNewEnemy = () => {
  const [currentNotification, setCurrentNotification] = useState<NewEnemyNotification | null>(null)
  const [isCardOpen, setIsCardOpen] = useState(false)
  const seenEnemiesRef = useRef<Set<string>>(new Set())
  const audioRef = useRef<HTMLAudioElement | null>(null)

  // Initialize audio
  useEffect(() => {
    const audio = new Audio("/sounds/events/levelcomplete.mp3")
    audio.volume = 0.6
    audioRef.current = audio
    return () => {
      audio.pause()
      audioRef.current = null
    }
  }, [])

  const notifyNewEnemy = useCallback((enemyId: string, variant: EnemyVariant = "regular") => {
    const key = `${enemyId}-${variant}`

    // Only show notification for the first encounter of this enemy type + variant
    if (!seenEnemiesRef.current.has(key)) {
      seenEnemiesRef.current.add(key)
      setCurrentNotification({ enemyId, variant })

      // Play sound
      if (audioRef.current) {
        audioRef.current.currentTime = 0
        audioRef.current.play().catch((err) => {
          console.warn("Failed to play new enemy sound:", err)
        })
      }
    }
  }, [])

  const openCard = useCallback(() => {
    setIsCardOpen(true)
  }, [])

  const closeCard = useCallback(() => {
    setIsCardOpen(false)
    // Clear notification after closing card
    setCurrentNotification(null)
  }, [])

  const dismissNotification = useCallback(() => {
    setCurrentNotification(null)
  }, [])

  return {
    currentNotification,
    isCardOpen,
    notifyNewEnemy,
    openCard,
    closeCard,
    dismissNotification,
  }
}
